import { CSSProperties, useEffect, useState } from 'react';
import { useField } from 'informed';
import isFQDN from 'validator/lib/isFQDN';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { SelectInput } from 'components/common/form/common';

const findDomainsRegex = /[,;\s]*@?[^\s@]+\.[^\s,;]+[,;\s][,;\s]*/g;
const findExtraSeparatorsAtStartRegex = /^[<.,;'\\\s]*/;
const findExtraSeparatorsAtEndRegex = /[>.,;'\\\s]*$/;
const findLineBreaksRegex = /(\r\n|\n|\r)/gm;

export interface DomainsProps {
  disabled?: boolean;
  field: string;
  id?: string;
  isRequired?: boolean;
  onChange?: (value: any) => void;
  style?: CSSProperties;
}

const Domains = (props: DomainsProps) => {
  const { messages, theme } = useReduxState();

  const [inputValue, setInputValue] = useState('');

  const { fieldState, fieldApi, render, ref, userProps } = useField({
    ...props
  });

  const {
    disabled,
    existingUsers,
    id,
    isRequired,
    onChange,
    style,
    ...params
  } = userProps;

  const fieldValue = fieldState.value as string[];

  useEffect(() => {
    if (!fieldState.touched) setInputValue('');
  }, [fieldState.touched]);

  const handleChange = (arr) => {
    const value =
      arr && arr.length ? arr.map((obj) => obj.value?.toLowerCase()) : null;
    fieldApi.setValue(value);

    if (value) fieldApi.setError(undefined);
    else if (!value && isRequired)
      fieldApi.setError(messages.commonFieldRequired);

    if (onChange) onChange(value);
  };

  const handleInputChange = (currentValue, { action }) => {
    if (action === 'menu-close') return;
    let value = currentValue;
    if (action === 'input-blur') {
      value = inputValue + ' ';
    }

    const domainMatches = value.match(findDomainsRegex);

    const newDomains = [];

    if (domainMatches) {
      // handle each match
      value = value.replace(findDomainsRegex, (domain, length, string) => {
        // remove extra separators
        let parsedDomain = domain
          .replace(findExtraSeparatorsAtStartRegex, '')
          .replace(findExtraSeparatorsAtEndRegex, '');
        if (parsedDomain.startsWith('@')) {
          parsedDomain = parsedDomain.slice(1);
        }
        // validate domain
        if (isFQDN(parsedDomain)) {
          // filter out duplicates
          if (
            !(fieldValue || []).includes(parsedDomain.toLowerCase()) &&
            !newDomains.includes(parsedDomain.toLowerCase())
          ) {
            newDomains.push(parsedDomain.toLowerCase());
          }
          return '';
        } else {
          return domain;
        }
      });
    }

    value = value.replace(findLineBreaksRegex, '').trim();

    const domains = [...(fieldValue || []), ...newDomains];

    setInputValue(value);

    fieldApi.setValue(domains);
    fieldApi.setTouched(true);
  };

  const handleKeyDown = (ev) => {
    // prevent enter from submitting form
    if (ev.keyCode === 13) {
      ev.preventDefault();
      // hack to check input value
      if (!ref.current.state.loadedOptions?.length) {
        handleInputChange(fieldValue, {
          action: 'input-blur'
        });
      }
    }
  };

  return render(
    <div style={style} onKeyDown={handleKeyDown}>
      <SelectInput
        hideDropdownIndicator={true}
        id={id}
        inputId={`${id}-input`}
        inputValue={inputValue}
        isClearable={false}
        isDisabled={disabled}
        isMulti={true}
        isSearchable={true}
        loadingMessage={() => null}
        noOptionsMessage={() => null}
        onChange={handleChange}
        onInputChange={handleInputChange}
        placeholder="example.com, example2.com, ..."
        selectRef={ref}
        value={
          fieldValue
            ? fieldValue.map((value) => ({
                label: value,
                value: value
              }))
            : null
        }
        {...params}
      />

      {fieldState.error ? (
        <div className="error">{fieldState.error}</div>
      ) : fieldValue?.length ? (
        <div className="domains-count">
          {getMessageWithPlaceholders({
            message: messages.commonNumberOfDomains,
            placeholders: { numberOfDomains: fieldValue.length }
          })}
        </div>
      ) : null}

      <style jsx>{`
        .error {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
        .domains-count {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default Domains;
